import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { reportService, transactionService } from '../services/services';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#6366f1', '#a855f7'];

const Dashboard = () => {
  const [summary, setSummary] = useState({ total_income: 0, total_expense: 0, balance: 0 });
  const [topDayExpense, setTopDayExpense] = useState(null);
  const [topMonthExpense, setTopMonthExpense] = useState(null);
  const [topMonthIncome, setTopMonthIncome] = useState(null);
  const [expensesByCategory, setExpensesByCategory] = useState([]);
  const [cashflow, setCashflow] = useState([]);
  const [lastTransactions, setLastTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  // Função para obter data atual no formato brasileiro
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date();
      let from, to;

      if (period === 'day') {
        const todayStr = getTodayDate();
        from = todayStr;
        to = todayStr;
      } else if (period === 'week') {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        from = format(weekAgo, 'yyyy-MM-dd', { locale: ptBR });
        to = getTodayDate();
      } else if (period === 'month') {
        from = format(startOfMonth(today), 'yyyy-MM-dd', { locale: ptBR });
        to = format(endOfMonth(today), 'yyyy-MM-dd', { locale: ptBR });
      } else {
        from = format(startOfMonth(new Date(today.getFullYear(), 0, 1)), 'yyyy-MM-dd', { locale: ptBR });
        to = format(endOfMonth(new Date(today.getFullYear(), 11, 31)), 'yyyy-MM-dd', { locale: ptBR });
      }

      const [summaryData, topDay, topMonthExp, topMonthInc, expenses, cashflowData, transactions] = await Promise.all([
        reportService.summary(from, to),
        reportService.topDayExpense(),
        reportService.topMonthExpense(),
        reportService.topMonthIncome(),
        reportService.expensesByCategory(from, to),
        reportService.cashflow(from, to, period === 'year' ? 'month' : 'day'),
        transactionService.list({ from, to, limit: 10 }),
      ]);

      setSummary(summaryData);
      setTopDayExpense(topDay);
      setTopMonthExpense(topMonthExp);
      setTopMonthIncome(topMonthInc);
      setExpensesByCategory(expenses);
      setCashflow(cashflowData);
      setLastTransactions(transactions.slice(0, 10));
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Função para formatar data evitando problemas de timezone
  const formatDate = (dateString, formatStr = 'dd/MM/yyyy') => {
    if (!dateString) return '';
    
    // Se já está no formato YYYY-MM-DD, usar diretamente sem criar Date com timezone
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      // Criar data local diretamente sem passar por UTC
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return format(date, formatStr, { locale: ptBR });
    }
    
    // Se a data vem como YYYY-MM-DD com hora, criar como data local
    const date = dateString.includes('T') 
      ? parseISO(dateString) 
      : new Date(dateString + 'T00:00:00');
    return format(date, formatStr, { locale: ptBR });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-10 h-10 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm font-medium text-gray-400">Carregando dados financeiros...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8 px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Dashboard</h1>
          <p className="text-xs sm:text-sm text-gray-400 mt-1">Visão geral das finanças</p>
        </div>
        <div className="relative inline-block w-full sm:w-auto">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none z-10">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-full sm:w-auto pl-9 sm:pl-10 pr-9 sm:pr-10 py-2.5 sm:py-3 text-xs sm:text-sm bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-semibold text-gray-100 cursor-pointer hover:border-gray-600 transition-all duration-200 appearance-none sm:min-w-[180px]"
          >
            <option value="day" className="bg-gray-800">Hoje</option>
            <option value="week" className="bg-gray-800">Última Semana</option>
            <option value="month" className="bg-gray-800">Este Mês</option>
            <option value="year" className="bg-gray-800">Este Ano</option>
          </select>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        <div className="stat-card p-4 sm:p-6">
          <div className="stat-card-header">
            <span className="stat-card-label text-[10px] sm:text-xs">Total de Entradas</span>
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-900/30 rounded-lg flex items-center justify-center border border-emerald-800/50">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
          <div className="stat-card-value text-xl sm:text-2xl text-emerald-400">{formatCurrency(summary.total_income)}</div>
        </div>
        
        <div className="stat-card p-4 sm:p-6">
          <div className="stat-card-header">
            <span className="stat-card-label text-[10px] sm:text-xs">Total de Saídas</span>
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-900/30 rounded-lg flex items-center justify-center border border-red-800/50">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
              </svg>
            </div>
          </div>
          <div className="stat-card-value text-xl sm:text-2xl text-red-400">{formatCurrency(summary.total_expense)}</div>
        </div>
        
        <div className="stat-card p-4 sm:p-6 sm:col-span-2 lg:col-span-1">
          <div className="stat-card-header">
            <span className="stat-card-label text-[10px] sm:text-xs">Saldo</span>
            <div className={`w-8 h-8 sm:w-10 sm:h-10 ${summary.balance >= 0 ? 'bg-blue-900/30 border-blue-800/50' : 'bg-orange-900/30 border-orange-800/50'} rounded-lg flex items-center justify-center border`}>
              <svg className={`w-4 h-4 sm:w-5 sm:h-5 ${summary.balance >= 0 ? 'text-blue-400' : 'text-orange-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className={`stat-card-value text-xl sm:text-2xl ${summary.balance >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
            {formatCurrency(summary.balance)}
          </div>
        </div>
      </div>

      {/* Informações Destacadas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        <div className="card p-4 sm:p-6">
            <div className="flex items-start justify-between mb-3 sm:mb-4">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Dia com Maior Gasto</p>
              <p className="text-sm sm:text-lg font-bold text-gray-100 truncate">
                {topDayExpense?.day ? formatDate(topDayExpense.day) : 'N/A'}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0 border border-purple-800/50 ml-2">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <p className="text-lg sm:text-xl font-bold text-purple-400">{formatCurrency(topDayExpense?.total || 0)}</p>
        </div>
        
        <div className="card p-4 sm:p-6">
            <div className="flex items-start justify-between mb-3 sm:mb-4">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Mês com Maior Gasto</p>
              <p className="text-sm sm:text-lg font-bold text-gray-100 truncate">
                {topMonthExpense?.month ? formatDate(topMonthExpense.month + '-01', 'MMM/yyyy') : 'N/A'}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-pink-900/30 rounded-lg flex items-center justify-center flex-shrink-0 border border-pink-800/50 ml-2">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <p className="text-lg sm:text-xl font-bold text-pink-400">{formatCurrency(topMonthExpense?.total || 0)}</p>
        </div>
        
        <div className="card p-4 sm:p-6 sm:col-span-2 lg:col-span-1">
            <div className="flex items-start justify-between mb-3 sm:mb-4">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Mês com Maior Entrada</p>
              <p className="text-sm sm:text-lg font-bold text-gray-100 truncate">
                {topMonthIncome?.month ? formatDate(topMonthIncome.month + '-01', 'MMM/yyyy') : 'N/A'}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-teal-900/30 rounded-lg flex items-center justify-center flex-shrink-0 border border-teal-800/50 ml-2">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-lg sm:text-xl font-bold text-teal-400">{formatCurrency(topMonthIncome?.total || 0)}</p>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
        <div className="card p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-bold text-white mb-4 sm:mb-6">Fluxo Financeiro</h2>
          <div className="w-full" style={{ height: '250px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cashflow} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey={period === 'year' ? 'month' : 'day'} 
                  stroke="#9ca3af"
                  tick={{ fill: '#9ca3af', fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis 
                  stroke="#9ca3af"
                  tick={{ fill: '#9ca3af', fontSize: 10 }}
                  width={50}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ 
                    borderRadius: '8px', 
                    border: '1px solid #374151',
                    backgroundColor: '#111827',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
                    color: '#f3f4f6',
                    fontSize: '12px',
                    padding: '8px'
                  }}
                />
                <Legend 
                  wrapperStyle={{ color: '#9ca3af', fontSize: '12px' }}
                  iconSize={12}
                />
                <Line type="monotone" dataKey="income" stroke="#10b981" strokeWidth={2} name="Entradas" dot={{ r: 2 }} />
                <Line type="monotone" dataKey="expense" stroke="#ef4444" strokeWidth={2} name="Saídas" dot={{ r: 2 }} />
                <Line type="monotone" dataKey="balance" stroke="#3b82f6" strokeWidth={2} name="Saldo" dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-bold text-white mb-4 sm:mb-6">Despesas por Categoria</h2>
          {expensesByCategory.length > 0 ? (
            <div className="w-full" style={{ height: '250px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={expensesByCategory}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => {
                      const percentValue = (percent * 100).toFixed(0);
                      return `${name.length > 10 ? name.substring(0, 10) + '...' : name} ${percentValue}%`;
                    }}
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="total"
                  >
                    {expensesByCategory.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ 
                      borderRadius: '8px', 
                      border: '1px solid #374151',
                      backgroundColor: '#111827',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
                      color: '#f3f4f6',
                      fontSize: '12px',
                      padding: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center" style={{ height: '250px' }}>
              <div className="text-center">
                <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p className="text-xs sm:text-sm text-gray-500">Nenhuma despesa no período</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Últimas Transações */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-bold text-white">Últimas Transações</h2>
        </div>
        <div className="overflow-x-auto -mx-4 sm:-mx-6 px-4 sm:px-6">
          <table className="min-w-full divide-y divide-gray-800">
            <thead>
              <tr>
                <th className="table-header px-3 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs">Data</th>
                <th className="table-header px-3 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs">Descrição</th>
                <th className="table-header px-3 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs hidden sm:table-cell">Categoria</th>
                <th className="table-header px-3 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs">Tipo</th>
                <th className="table-header px-3 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs text-right">Valor</th>
              </tr>
            </thead>
            <tbody className="bg-gray-900 divide-y divide-gray-800">
              {lastTransactions.length > 0 ? (
                lastTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-800 transition-colors">
                    <td className="table-cell px-3 sm:px-6 py-3 sm:py-4 text-gray-400 font-medium text-xs sm:text-sm">
                      {formatDate(transaction.date)}
                    </td>
                    <td className="table-cell px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm max-w-[120px] sm:max-w-none truncate">
                      {transaction.description || <span className="text-gray-600">—</span>}
                    </td>
                    <td className="table-cell px-3 sm:px-6 py-3 sm:py-4 text-gray-400 text-xs sm:text-sm hidden sm:table-cell">
                      {transaction.category_name || <span className="text-gray-600">—</span>}
                    </td>
                    <td className="table-cell px-3 sm:px-6 py-3 sm:py-4">
                      <span className={`badge-${transaction.type === 'income' ? 'success' : 'danger'} text-[10px] sm:text-xs`}>
                        {transaction.type === 'income' ? 'Entrada' : 'Saída'}
                      </span>
                    </td>
                    <td className={`table-cell px-3 sm:px-6 py-3 sm:py-4 text-right font-semibold text-xs sm:text-sm ${
                      transaction.type === 'income' ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {transaction.type === 'income' ? '+' : '−'} {formatCurrency(transaction.amount)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-4 sm:px-6 py-8 sm:py-12 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-xs sm:text-sm text-gray-500">Nenhuma transação encontrada</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
